import { Link, useNavigate, useParams } from 'react-router-dom';
import { Character, ProgressBar } from '../components/ui';
import { CharacterMoment } from '../components/characterMoments';
import { CERTIFICATE_CATALOG, CERTIFICATE_SETS, type CertificateSetId } from '../domain/certificateCollection';
import { formatDateKo } from '../domain/rules';
import { useReaction } from '../reactions/ReactionProvider';
import { useSound } from '../sound/SoundProvider';
import { useDemo } from './DemoProvider';
import {
  demoCertificateById,
  demoCertificateCharacterAsset,
  demoCertificateStatus,
  demoCertificateUnlocked,
  demoCollectionProgress,
  demoSetComplete,
} from './demoCertificateCollection';
import { demoProgress } from './demoExperience';
import { deriveAdventureResult } from './adventureEngine';
import { DemoFrame, DemoLandingScreen } from './demoScreens';

const A = '/assets';

const DEMO_CHARACTER_CAST = [
  ['/assets/characters/haneul/haneul_wave_v1.png', '첫 인사', '호기심'],
  ['/assets/characters/haneul/haneul_report_v1.png', '기록가', '관찰'],
  ['/assets/characters/haneul/haneul_think_v1.png', '생각가', '선택'],
  ['/assets/characters/haneul/haneul_piggy_v1.png', '저축가', '목표'],
  ['/assets/characters/haneul/haneul_run_v1.png', '탐험가', '행동'],
  ['/assets/characters/haneul/haneul_openarms_v1.png', '보관가', '기억'],
  ['/assets/characters/haneul/haneul_heart_v1.png', '응원가', '연결'],
  ['/assets/characters/haneul/haneul_jump_v1.png', '도전자', '도전'],
  ['/assets/characters/haneul/haneul_peace_v1.png', '균형 선택', '균형'],
  ['/assets/characters/haneul/haneul_thumbs_v1.png', '확신의 선택', '통제감'],
  ['/assets/characters/haneul/haneul_surprise_v1.png', '발견자', '발견'],
  ['/assets/characters/haneul/haneul_celebrate_v1.png', '축하대장', '성취'],
  ['/assets/characters/haneul/haneul_star_v1.png', '별빛 수료자', '자부심'],
  ['/assets/characters/sage/sage_research_v1.png', '경제 연구 설옹', '이해'],
  ['/assets/characters/sage/sage_binocular_v1.png', '관찰 설옹', '비교'],
  ['/assets/characters/sage/sage_calculator_v1.png', '계산 설옹', '판단'],
  ['/assets/characters/sage/sage_checklist_v1.png', '인증 설옹', '회고'],
  ['/assets/characters/friends/arctic_fox_v1.png', '북극여우', '탐험 힌트'],
] as const;

function rarityLabel(rarity: string, category: string) {
  return `${rarity} · ${category}`;
}

export function DemoCertificateCollectionScreen() {
  const navigate = useNavigate();
  const { session, claimSetReward } = useDemo();
  const { emit } = useReaction();
  const journey = demoProgress(session);
  const collection = demoCollectionProgress(session);
  const adventure = deriveAdventureResult(session);

  if (!session.started) return <DemoLandingScreen />;

  const claimSet = async (setId: CertificateSetId) => {
    const complete = demoSetComplete(session, setId);
    if (!complete || session.claimedSetIds.includes(setId)) return;
    claimSetReward(setId);
    await emit('badge.unlocked', { silent: !session.soundRequested });
  };

  return <DemoFrame className="demo-hall-screen honor-hall-screen">
    <section className="honor-hall-hero demo-hall-hero">
      <div className="honor-hall-copy">
        <p className="eyebrow">DEMO EPILOGUE · MISSION CERTIFICATE COLLECTION</p>
        <h1>{adventure.resultTitle}의 여행이<br/>수료증과 배지로 남아요</h1>
        <p>{adventure.resultCopy} 선택 결과와 수집한 탐험 아이템이 명예의 전당 컬렉션으로 이어집니다.</p>
        <div className="honor-hall-metrics">
          <span><small>여정 진행</small><strong>{journey.completed} / 8</strong></span>
          <span><small>보관 완료</small><strong>{collection.claimed} / {collection.total}</strong></span>
          <span><small>발급 가능</small><strong>{collection.ready}개</strong></span>
          <span><small>컬렉션 보너스</small><strong>{session.collectionXp} XP</strong></span><span><small>비밀 배지</small><strong>{adventure.hiddenBadgeUnlocked ? '진실의 눈꽃' : '미발견'}</strong></span>
        </div>
        <ProgressBar value={collection.percent}/>
      </div>
      <div className="honor-hall-characters" aria-label="명예의 전당을 안내하는 캐릭터들">
        <Character src={`${A}/characters/haneul/haneul_celebrate_v1.png`} alt="수료증 컬렉션을 축하하는 하늘이" />
        <Character src={`${A}/characters/friends/arctic_fox_v1.png`} alt="배지 보드를 안내하는 북극여우" />
        <Character src={`${A}/characters/sage/sage_checklist_v1.png`} alt="수료 기록을 확인하는 설옹" />
      </div>
    </section>

    {!journey.complete && <section className="demo-hall-lock-note">
      <Character src={`${A}/characters/haneul/haneul_run_v1.png`} alt="정상을 향해 달리는 하늘이" />
      <div><small>정상 도착 전 미리보기</small><strong>8단계를 완주하면 12종 수료증이 모두 발급 가능 상태로 열립니다</strong><p>현재 진행에 따라 일부 수료증은 먼저 발급할 수 있습니다.</p></div>
      <button onClick={() => navigate('/demo/child')}>남은 장면 체험하기</button>
    </section>}

    <CharacterMoment
      src={`${A}/characters/sage/sage_checklist_v1.png`}
      alt="데모 수료 컬렉션을 설명하는 설옹"
      eyebrow="설옹의 데모 해설"
      title="미션을 끝내는 순간은 점수가 아니라 나의 힘에 이름을 붙이는 순간이에요"
      copy="수료증을 발급하면 전용 배지가 보드에 장착되고, 캐릭터 성장 순간과 주제별 컬렉션 프레임이 함께 열립니다."
      tone="gold"
      compact
    />

    <section className="character-cast-section">
      <div className="section-title-row"><div><span>CHARACTER MOMENT ALBUM</span><h2>데모 캐릭터 성장 도감</h2></div><small>보관한 수료증이 늘수록 18개의 표정과 역할이 컬러로 깨어납니다</small></div>
      <div className="character-cast-scroll" role="list" aria-label="데모 캐릭터 성장 도감">
        {DEMO_CHARACTER_CAST.map(([src, label, role], index) => {
          const revealed = index < Math.max(4, collection.claimed + 4);
          return <article key={src} className={`character-cast-card ${revealed ? 'revealed' : 'locked'}`} role="listitem">
            <Character src={src} alt={revealed ? `${label} 캐릭터` : ''}/>
            <strong>{revealed ? label : '잠긴 순간'}</strong><small>{revealed ? role : `수료증 ${Math.max(1, index - 3)}개 필요`}</small>
          </article>;
        })}
      </div>
    </section>

    <section className="certificate-set-section">
      <div className="section-title-row"><div><span>COLLECTION SET</span><h2>주제별 컬렉션 프레임</h2></div><small>세 개의 수료증을 모으면 프레임과 보너스 XP가 열립니다</small></div>
      <div className="certificate-set-grid">
        {CERTIFICATE_SETS.map((set) => {
          const progress = set.certificateIds.filter((id) => session.claimedCertificateIds.includes(id)).length;
          const complete = demoSetComplete(session, set.id);
          const rewarded = session.claimedSetIds.includes(set.id);
          return <article key={set.id} className={`certificate-set-card set-${set.id} ${complete ? 'complete' : ''}`}>
            <div className="set-card-medal"><img src={`${A}/certificates/mrm_cert_02_goldmedal_v01.png`} alt=""/><strong>{progress}/{set.certificateIds.length}</strong></div>
            <div className="set-card-copy"><small>{set.frameName}</small><h3>{set.title}</h3><p>{set.description}</p><div className="set-progress-dots">{set.certificateIds.map((id) => <i key={id} className={session.claimedCertificateIds.includes(id) ? 'filled' : ''}/>)}</div></div>
            {complete
              ? <button type="button" className={rewarded ? 'set-reward claimed' : 'set-reward'} disabled={rewarded} onClick={() => void claimSet(set.id)}>{rewarded ? '프레임 보관 완료' : `세트 보너스 +${set.rewardXp} XP`}</button>
              : <span className="set-reward locked">수료증 {set.certificateIds.length - progress}개 더 필요</span>}
          </article>;
        })}
      </div>
    </section>

    <section className="certificate-gallery-section">
      <div className="section-title-row"><div><span>CERTIFICATE GALLERY</span><h2>12종 미션 수료증</h2></div><small>대표 수료증을 발급하고, 전체 해제 프리셋에서 모든 디자인을 확인할 수 있습니다</small></div>
      <div className="certificate-collection-grid">
        {CERTIFICATE_CATALOG.map((certificate) => {
          const status = demoCertificateStatus(session, certificate);
          return <Link key={certificate.id} to={`/demo/certificate/${certificate.id}`} className={`certificate-collection-card tone-${certificate.tone} ${status}`}>
            <div className="certificate-card-preview">
              <span className="certificate-card-rarity">{certificate.rarity}</span>
              <img className="certificate-card-badge" src={certificate.badgeAsset} alt=""/>
              <Character src={demoCertificateCharacterAsset(certificate.character)} alt="" className="certificate-card-character"/>
              <div className="certificate-card-seal">{String(certificate.order).padStart(2, '0')}</div>
            </div>
            <div className="certificate-card-info">
              <small>{certificate.category}</small><h3>{certificate.shortTitle}</h3>
              <p>{status === 'claimed' ? '데모 명예의 전당에 보관 완료' : status === 'ready' ? `지금 발급 가능 · +${certificate.rewardXp} XP` : certificate.unlockRule}</p>
              <strong>{status === 'claimed' ? '수료증 다시 보기' : status === 'ready' ? '발급 체험하기' : '해제 조건 보기'} <b>›</b></strong>
            </div>
          </Link>;
        })}
      </div>
    </section>

    <section className="certificate-badge-board-section">
      <div className="section-title-row"><div><span>SNOW BADGE BOARD</span><h2>데모 수료 배지 보드</h2></div><small>수료증을 발급할 때마다 해당 배지가 컬러로 장착됩니다</small></div>
      <div className="certificate-badge-board">
        <div className="badge-board-center"><img src={`${A}/certificates/mrm_cert_03_completionseal_v01.png`} alt="공식 인증 인장"/><strong>{collection.claimed}</strong><small>COLLECTED</small></div>
        {CERTIFICATE_CATALOG.map((certificate, index) => {
          const status = demoCertificateStatus(session, certificate);
          return <Link key={certificate.id} to={`/demo/certificate/${certificate.id}`} className={`badge-board-slot slot-${index + 1} ${status}`} aria-label={`${certificate.shortTitle} ${status}`}>
            <img src={certificate.badgeAsset} alt=""/><span>{index + 1}</span><small>{certificate.shortTitle}</small>
          </Link>;
        })}
      </div>
    </section>

    <div className="demo-hall-actions">
      <button onClick={() => navigate('/demo/child')}>8단계 장면 다시 보기</button>
      <button onClick={() => navigate('/demo/report')}>최종 성장 리포트</button>
      <Link to="/demo">데모 시작 화면</Link>
    </div>
  </DemoFrame>;
}

export function DemoMissionCertificateScreen() {
  const navigate = useNavigate();
  const { certificateId } = useParams();
  const { session, claimCertificate } = useDemo();
  const { emit } = useReaction();
  const { play } = useSound();
  const certificate = demoCertificateById(certificateId);
  const adventure = deriveAdventureResult(session);

  if (!session.started) return <DemoLandingScreen />;
  if (!certificate) return <DemoFrame className="demo-certificate-screen"><section className="demo-certificate-error"><h1>수료증을 찾지 못했어요</h1><button onClick={() => navigate('/demo/hall')}>명예의 전당으로</button></section></DemoFrame>;

  const displayTitle = certificate.id === 'snowball-master' ? adventure.certificateTitle : certificate.title;
  const status = demoCertificateStatus(session, certificate);
  const unlocked = demoCertificateUnlocked(session, certificate);
  const claimed = status === 'claimed';
  const issueDate = session.updatedAt;
  const code = `MRM-DEMO-${String(certificate.order).padStart(2, '0')}-HANEUL`;
  const stats = [
    { label: '세계관 여정', value: `${session.completedStageIds.length}/8`, icon: `${A}/ui-icons/mrm_ui_12a_mission_v01.png` },
    { label: '행동 보상', value: `${session.points}P`, icon: `${A}/badges/mrm_badge_02_missionrewardstar_v01.png` },
    { label: '수료 컬렉션', value: `${session.claimedCertificateIds.length}개`, icon: `${A}/certificates/mrm_cert_02_goldmedal_v01.png` },
    { label: '컬렉션 XP', value: `${session.collectionXp} XP`, icon: `${A}/ui-icons/mrm_ui_12c_saving_v01.png` },
  ];

  const issue = async () => {
    if (!unlocked || claimed) return;
    claimCertificate(certificate.id);
    if (session.soundRequested) await play(certificate.rarity === '전설' ? 'daily-complete' : 'mission-complete');
  };

  return <DemoFrame className={`demo-certificate-screen certificate-screen certificate-detail tone-${certificate.tone}`}>
    <section className="demo-certificate-nav"><button onClick={() => navigate('/demo/hall')}>‹ 명예의 전당</button><span>DEMO CERTIFICATE {String(certificate.order).padStart(2, '0')} / 12</span></section>

    {!unlocked && <section className="certificate-lock-banner demo-certificate-banner">
      <Character src={`${A}/characters/sage/sage_checklist_v1.png`} alt="잠금 조건을 안내하는 설옹"/>
      <div><span>아직 잠긴 데모 수료증</span><h2>{certificate.unlockRule}</h2><p>8단계 체험을 이어가거나 ‘전체 해제’ 프리셋을 선택하면 디자인과 발급 흐름을 확인할 수 있습니다.</p></div>
    </section>}
    {unlocked && !claimed && <section className="certificate-ready-banner demo-certificate-banner">
      <Character src={`${A}/characters/haneul/haneul_surprise_v1.png`} alt="새 수료증을 발견한 하늘이"/>
      <div><span>새 수료증 발견!</span><h2>전용 배지와 {certificate.rewardXp} XP를 명예의 전당에 보관해요</h2><p>데모 보상은 실제 사용자 계정이나 서버 포인트에 반영되지 않습니다.</p></div>
      <button className="primary-button" onClick={() => void issue()}>수료증 발급 체험</button>
    </section>}

    <section className={`certificate-stage demo-certificate-stage ${!unlocked ? 'locked-preview' : ''}`}>
      <article className={`certificate-sheet certificate-variant-${certificate.tone}`} role="document" aria-label={`하늘이의 ${displayTitle}`}>
        {!claimed && <div className="certificate-preview-watermark">{unlocked ? 'READY TO ISSUE' : 'LOCKED PREVIEW'}</div>}
        <span className="certificate-corner corner-a"/><span className="certificate-corner corner-b"/><span className="certificate-corner corner-c"/><span className="certificate-corner corner-d"/>
        <header className="certificate-official-header">
          <div className="certificate-brand-lockup"><span>MY</span><div><strong>MY real Money</strong><small>DEMO MISSION CERTIFICATE COLLECTION</small></div></div>
          <img className="certificate-medal" src={certificate.badgeAsset} alt={`${certificate.shortTitle} 배지`}/>
          <div className="certificate-authority"><small>{certificate.sealLabel}</small><strong>{rarityLabel(certificate.rarity, certificate.category)}</strong></div>
        </header>
        <section className="certificate-title-block"><p>{certificate.id === 'snowball-master' ? adventure.resultCopy : certificate.description}</p><h1>{displayTitle}</h1><span>{certificate.id === 'snowball-master' ? `${adventure.resultTitle} · 선택 기반 맞춤 인증` : certificate.subtitle}</span></section>
        <section className="certificate-recipient"><small>수료자</small><h2>하늘이</h2><i/><p>{certificate.achievementCopy}</p></section>
        <section className="certificate-achievement-grid">{stats.map((item) => <div key={item.label}><img src={item.icon} alt=""/><span>{item.label}</span><strong>{item.value}</strong></div>)}</section>
        <section className="certificate-canon-quote"><span>MRM 성장 원칙 · {certificate.category}</span><blockquote>“{certificate.id === 'snowball-master' ? adventure.snowsLine : '돈의 크기보다 선택의 깊이가 스노우볼을 키웁니다.'}”</blockquote>{certificate.id === 'snowball-master' && adventure.hiddenBadgeUnlocked && <b className="certificate-secret-seal">비밀 인장 · 진실의 눈꽃</b>}</section>
        <footer className="certificate-footer">
          <div className="certificate-character certificate-character-haneul"><Character src={demoCertificateCharacterAsset(certificate.character)} alt={`${certificate.shortTitle} 캐릭터`}/></div>
          <div className="certificate-issue-info"><small>발급번호</small><strong>{claimed ? code : '발급 대기 중'}</strong><span>{claimed ? formatDateKo(issueDate.slice(0, 10)) : certificate.unlockRule}</span></div>
          <div className="certificate-signature"><small>MY real Money 통합 데모</small><strong>NEXPA.AI</strong><span>OFFICIAL DEMO CERTIFICATE</span></div>
          <img className="certificate-seal" src={`${A}/certificates/mrm_cert_03_completionseal_v01.png`} alt="공식 수료 인증 도장"/>
          <div className="certificate-character certificate-character-sage"><Character src={`${A}/characters/sage/sage_checklist_v1.png`} alt="수료 기록을 인증하는 설옹"/></div>
        </footer>
      </article>
    </section>

    <section className="demo-certificate-note"><strong>{claimed ? '데모 명예의 전당에 보관되었습니다' : '수료증 컬렉션 체험 안내'}</strong><p>{claimed ? '배지 보드와 캐릭터 도감의 해제 상태가 즉시 갱신됩니다.' : '발급 가능 조건을 충족하면 최초 1회 XP가 지급되고 수료 배지가 컬렉션 보드에 장착됩니다.'}</p></section>
    <div className="demo-certificate-actions">
      {claimed ? <button onClick={() => window.print()}>인쇄·PDF 미리보기</button> : unlocked ? <button onClick={() => void issue()}>수료증 발급 체험</button> : <button onClick={() => navigate('/demo/child')}>연계 장면 체험하기</button>}
      <button onClick={() => navigate('/demo/hall')}>명예의 전당으로</button>
      <button onClick={() => navigate('/demo/report')}>최종 리포트 보기</button>
    </div>
  </DemoFrame>;
}
